"use strict"

import React, { useEffect, useReducer, useState } from "react"
import { Button, Form, FormControl } from "react-bootstrap"
import moment from "moment"

import { getLocaleFormattedUserString, RequiredFieldWarning } from "../internationalization/LocaleFormatted"
import AreYouSureModal from "../widgets/AreYouSureModal"
import { NewActionBody } from "./modals/ModalsParts"
import { withAppSettingsConfig } from "../../config/AppSettingsConfigProvider"
import ReactSelectionElement from "../../shared_components/widgets/ReactSelectionElement"
import operatorActionChoices from "./defaultActions"
import ToggleSwitch from "../fermmanagement/modals/StartRunModal/ToggleSwitch"

const timeformat_display = "YYYY-MM-DDTHH:mm:ss" // For the form input
const timeformat_api = "YYYY-MM-DDTHH:mm:ssZZ" // For sending to the backend with tz offset

let initialState = {
  isLoading: false,
  useDefaultActions: false,
}

const initialWarningState = {
  actionWarn: false,
  commentWarn: false,
  timestampWarn: false,
  priorityWarn: false,
  actorWarn: false,
}

function ActionForm({
  actors,
  reactor,
  createUpdateOrDeleteAction,
  actionErr,
  intl,
  actionObjToEdit,
  isEditMode,
  onComplete = () => {},
  config, // From AppSettingsConfigProvider
  selectedAssociateReactors,
  parentReset,
}) {
  const [defaultActionOptions, setDefaultActionOptions] = useState([])

  useEffect(() => {
    const globalActions = operatorActionChoices?.["*"] || []
    const siteActions = operatorActionChoices?.[config?.site] || []
    setDefaultActionOptions([...globalActions, ...siteActions].sort())
  }, [config])

  if (isEditMode) {
    const { id, action, comment, timestamp, priority, actor } = actionObjToEdit
    initialState = {
      ...initialState,
      id: id,
      action: action,
      comment: comment,
      timestamp: moment(timestamp).format(timeformat_display),
      priority: priority,
      actor: actor.username,
    }
  } else {
    initialState = {
      ...initialState,
      action: "",
      comment: "",
      timestamp: moment().local().format(timeformat_display),
      priority: "NORM",
      actor: (config?.user || Object.values(actors)[0]).username,
    }
  }

  const formReducer = (state, action) => {
    switch (action.type) {
      case "field": {
        return {
          ...state,
          [action.field]: action.value,
        }
      }
      case "clear": {
        return {
          ...state,
          ...initialState,
          timestamp: isEditMode ? initialState.timestamp : moment().local().format(timeformat_display),
          ...initialWarningState,
        }
      }
      case "submit": {
        return {
          ...state,
          ...initialWarningState,
          isLoading: true,
        }
      }
      case "invalid-form": {
        return {
          ...state,
          isLoading: false,
        }
      }
    }
  }

  const [state, dispatch] = useReducer(formReducer, {
    ...initialState,
    ...initialWarningState,
  })

  const {
    isLoading,
    action,
    comment,
    timestamp,
    priority,
    actor,
    useDefaultActions,
    actionWarn,
    commentWarn,
    timestampWarn,
    priorityWarn,
    actorWarn,
  } = state

  // Create unique toggle switch ids so that Form.Check element (easier to style) behaves when ActionEditModal and OAJ are both open
  const toggleId = `action-toggle-${isEditMode ? "modal" : "main"}`

  const updateFormValue = (e, field, value, warning, warningField) => {
    e && e.preventDefault()
    dispatch({ type: "field", field: field, value: value })
    if (warning) {
      dispatch({ type: "field", field: warningField, value: false })
    }
  }

  const submitForm = (e, data) => {
    e && e.preventDefault()
    !isEditMode && initializeForm()
    setVerifySubmitModal(false)
    createUpdateOrDeleteAction(data, isEditMode ? "update" : "create", onComplete)
  }

  const cancelForm = (e) => {
    e && e.preventDefault()
    setVerifySubmitModal(false)
    dispatch({ type: "invalid-form" })
  }

  const initializeForm = () => {
    dispatch({ type: "clear" })
  }

  useEffect(() => {
    if (!isEditMode) {
      dispatch({
        type: "field",
        field: "timestamp",
        value: moment().local().format(timeformat_display),
      })
    }
  }, [reactor])

  useEffect(() => {
    if (actionErr) {
      dispatch({ type: "invalid-form" })
    }
  }, [actionErr])

  const [data, setData] = useState({})
  const [verifySubmitModal, setVerifySubmitModal] = useState(false)
  const validateForm = (e) => {
    e.preventDefault()
    dispatch({ type: "submit" })

    let validSubmission = true
    const fieldsToValidate = [
      { value: comment, field: "commentWarn" },
      { value: action, field: "actionWarn" },
      { value: timestamp, field: "timestampWarn" },
      { value: priority, field: "priorityWarn" },
      { value: actor, field: "actorWarn" },
    ]

    fieldsToValidate.forEach((field) => {
      if (field["value"] === "" || !field["value"]) {
        validSubmission = false
        dispatch({ type: "field", field: field["field"], value: true })
      }
    })

    if (validSubmission) {
      let data = {
        comment: comment,
        action: action,
        timestamp: moment(timestamp).format(timeformat_api),
        priority: priority,
        actor: actor,
        source: isEditMode
          ? reactor
          : selectedAssociateReactors?.length > 0
            ? [reactor, ...selectedAssociateReactors]
            : [reactor],
      }

      if (isEditMode) {
        data.id = actionObjToEdit.id
      }

      setData(data)
      setVerifySubmitModal(true)
    } else {
      dispatch({ type: "invalid-form" })
    }
  }

  const priorityOptions = [
    { value: "NORM", label: intl.formatMessage({ id: "general.priority.normal", defaultMessage: "Normal" }) },
    {
      value: "CRIT",
      label: intl.formatMessage({ id: "general.priority.critical", defaultMessage: "Critical" }),
    },
  ]

  return (
    <>
      {verifySubmitModal && (
        <AreYouSureModal
          show={verifySubmitModal}
          onHide={(e) => cancelForm(e)}
          data={data}
          modalBody={
            <NewActionBody data={{ ...data, actor: getLocaleFormattedUserString(actors[actor], intl) }} />
          }
          submitForm={submitForm}
          cancelForm={cancelForm}
        />
      )}
      <Form>
        {/*Error message*/}
        {actionErr && (
          <div>
            <p className={"error-message"}>{intl.formatMessage({ id: "general.server.error" })}</p>
          </div>
        )}

        <div className={"row"} style={{ marginBottom: "4px" }}>
          {/*Time*/}
          <div className={"col-7"}>
            <label style={{ margin: "0" }}>
              {intl.formatMessage({ id: "general.time", defaultMessage: "Time" })}
            </label>
            :
            <RequiredFieldWarning warning={timestampWarn} />
            <FormControl
              type={"datetime-local"}
              step={"1"}
              value={timestamp}
              onChange={(e) =>
                updateFormValue(e, "timestamp", e.target.value, timestampWarn, "timestampWarn")
              }
            />
          </div>

          {/*Priority*/}
          <div className={"col-5"}>
            <ReactSelectionElement
              name={"priority"}
              label={intl.formatMessage({ id: "general.priority", defaultMessage: "Priority" })}
              getOptionLabel={(obj) => obj.label}
              list={priorityOptions}
              value={priorityOptions.find((p) => p.value === priority) || { value: "", label: "" }}
              onValueChange={(_, selection) =>
                updateFormValue(null, "priority", selection?.value, priorityWarn, "priorityWarn")
              }
              klass={""}
              isMulti={false}
              isClearable={false}
            />
            <RequiredFieldWarning warning={priorityWarn} />
          </div>
        </div>

        {/*Actor*/}
        <ActionInput
          intl={intl}
          id={"actor"}
          warnField={actorWarn}
          child={
            <Form.Control
              as="select"
              value={actor}
              onChange={(e) => updateFormValue(e, "actor", e.target.value, actorWarn, "actorWarn")}
            >
              {Object.values(actors).map((a) => (
                <option value={a.username} key={a.id}>
                  {getLocaleFormattedUserString(a, intl)}
                </option>
              ))}
            </Form.Control>
          }
        />

        {/*Action */}
        <div className="row" style={{ marginBottom: "5px" }}>
          <div className="col-12">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <label style={{ margin: "0" }}>
                {intl.formatMessage({ id: "general.action", defaultMessage: "Action" })}:
                <RequiredFieldWarning warning={actionWarn} />
              </label>
              <div>
                <ToggleSwitch
                  intl={intl}
                  idVal={toggleId}
                  value={useDefaultActions}
                  show={true}
                  labelInfo={{
                    id: `operatoraction.journal.selectFromDefaults`,
                    defaultMessage: "Explore default actions",
                  }}
                  onToggle={() => {
                    updateFormValue(null, "useDefaultActions", !useDefaultActions)
                    const match = defaultActionOptions.find((a) => a === action)
                    !useDefaultActions && !match && updateFormValue(null, "action", "")
                  }}
                  toggleStyle={{}}
                />
              </div>
            </div>
            {useDefaultActions ? (
              <ReactSelectionElement
                name={"defaultAction"}
                list={defaultActionOptions.map((action) => ({
                  value: action,
                }))}
                onValueChange={(_, opt) =>
                  updateFormValue(null, "action", opt?.value, actionWarn, "actionWarn")
                }
                value={
                  defaultActionOptions.find((a) => a === action)
                    ? { value: defaultActionOptions.find((a) => a === action) }
                    : null
                }
                isMulti={false}
                divStyle={{ marginBottom: "0" }}
              />
            ) : (
              <FormControl
                type="text"
                className="operator_action_input"
                placeholder={intl.formatMessage({
                  id: `operatoraction.newAction.actionInputPlaceholder`,
                })}
                value={action}
                onChange={(e) => updateFormValue(e, "action", e.target.value, actionWarn, "actionWarn")}
              />
            )}
          </div>
        </div>

        {/*Comment*/}
        <ActionInput
          intl={intl}
          id={"comment"}
          warnField={commentWarn}
          child={
            <FormControl
              type={"text"}
              className={"operator_action_input"}
              placeholder={intl.formatMessage({
                id: `operatoraction.newAction.commentInputPlaceholder`,
              })}
              value={comment}
              onChange={(e) => updateFormValue(e, "comment", e.target.value, commentWarn, "commentWarn")}
            />
          }
        />

        {/*Buttons*/}
        <div className={"row"} style={{ marginTop: "20px" }}>
          <div className={"col-12"}>
            <Button
              type={"reset"}
              variant={"outline-primary"}
              disabled={isLoading}
              onClick={(e) => {
                e.preventDefault()
                initializeForm()
                parentReset && parentReset()
              }}
            >
              {intl.formatMessage({ id: "general.clearButton", defaultMessage: "Clear" })}
            </Button>
            &nbsp;
            <Button variant={"primary"} type={"submit"} disabled={isLoading} onClick={(e) => validateForm(e)}>
              {intl.formatMessage({ id: "general.submitButton", defaultMessage: "Submit" })}
            </Button>
          </div>
        </div>
      </Form>
    </>
  )
}

const ActionInput = ({ intl, id, warnField, child }) => {
  return (
    <div className={"row"} style={{ marginBottom: "5px" }}>
      <div className={"col-12"}>
        <label style={{ margin: "0" }}>{intl.formatMessage({ id: `general.${id}` })}</label>
        :
        <RequiredFieldWarning warning={warnField} />
        {child}
      </div>
    </div>
  )
}

export default withAppSettingsConfig(ActionForm)
